// A floating window that can be dragged by a child marked `[data-drag-handle]`
// and resized by children marked `[data-resize-handle]` (value "nw"|"ne"|"sw"|"se"
// — which corner). CSS positions it (position: fixed, anchored top/left); dragging
// applies a translate offset on top of that, and resizing sets the element's
// width/height (and, for the top/left corners, adjusts the offset so the opposite
// corner stays put). Both are clamped to a min size and the viewport. Elements
// marked `[data-no-drag]` (e.g. the close button) don't start a drag.
//
// Interaction hardening:
// - Pointer capture keeps a drag/resize tracking even when the cursor crosses an
//   iframe or leaves the window — the browser routes every subsequent pointer
//   event to the capturing element regardless of what's underneath.
// - Any pointerup / pointercancel / lostpointercapture, or the window losing
//   focus, ends the interaction cleanly, so it can never get stuck to the cursor.
// - On a viewport resize (or display change) every open window pulls itself back
//   into reach so it can't be stranded off-screen.
// - Pointer-down raises a window above its siblings, always staying below the
//   modal layer (see the z-index band), so a modal reliably covers every window.
const DEFAULT_MIN_WIDTH = 360;
const DEFAULT_MIN_HEIGHT = 420;
const MARGIN = 12;

// A window's stacking layer, chosen via `[data-window-layer]`. Each layer has a
// base z-index; raising renormalizes windows within a layer across
// [base, base + BAND_SPAN] so a window never climbs out of its layer.
//   - default: below the modal band (backdrop 1040, content 1050) and above the
//     page's flyout/tooltip chrome (~1030) — the normal floating window.
//   - above-clinical: above the embedded clinical app's own modal/window layer,
//     for clinical-context windows (e.g. the charge window) that must stay
//     visible over the clinical chart. Deliberately not covered by portal modals.
const Z_BASES = { default: 1031, 'above-clinical': 1100 };
const BAND_SPAN = 8;

// Below this viewport width the window renders full-screen (see CSS) and
// drag/resize are disabled — dragging a full-screen sheet makes no sense.
const SMALL_SCREEN = 640;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

function zBaseOf(el) {
  return Z_BASES[el.getAttribute('data-window-layer')] || Z_BASES.default;
}

function zIndexOf(el) {
  return Number.parseInt(el.style.zIndex, 10) || zBaseOf(el);
}

// Raise `top` above the other windows in its own layer, reassigning a compact z
// sequence within that layer's band. Only same-layer windows reorder, so a window
// that opted into a higher layer keeps its tier no matter how others are raised.
function raiseWindow(top) {
  const base = zBaseOf(top);
  const layer = Array.from(document.querySelectorAll('draggable-window')).filter(
    (element) => zBaseOf(element) === base,
  );
  const others = layer.filter((element) => element !== top);
  others.sort((a, b) => zIndexOf(a) - zIndexOf(b));
  [...others, top].forEach((element, index) => {
    element.style.zIndex = String(base + Math.min(index, BAND_SPAN));
  });
}

class DraggableWindow extends HTMLElement {
  connectedCallback() {
    this.offsetX = 0;
    this.offsetY = 0;
    this.dragging = false;
    this.resizing = false;
    this.pointerId = null;

    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onEnd = this._onEnd.bind(this);
    this._onViewportResize = this._onViewportResize.bind(this);

    // Pointer capture routes move/up events to this element, so the handlers can
    // live on the element itself rather than the window.
    this.addEventListener('pointerdown', this._onPointerDown);
    this.addEventListener('pointermove', this._onPointerMove);
    this.addEventListener('pointerup', this._onEnd);
    this.addEventListener('pointercancel', this._onEnd);
    this.addEventListener('lostpointercapture', this._onEnd);
    window.addEventListener('resize', this._onViewportResize);
    window.addEventListener('blur', this._onEnd);

    // A freshly opened window sits on top of any already-open ones.
    raiseWindow(this);
  }

  disconnectedCallback() {
    this.removeEventListener('pointerdown', this._onPointerDown);
    this.removeEventListener('pointermove', this._onPointerMove);
    this.removeEventListener('pointerup', this._onEnd);
    this.removeEventListener('pointercancel', this._onEnd);
    this.removeEventListener('lostpointercapture', this._onEnd);
    window.removeEventListener('resize', this._onViewportResize);
    window.removeEventListener('blur', this._onEnd);
  }

  _minWidth() {
    return Number.parseInt(this.getAttribute('data-min-width'), 10) || DEFAULT_MIN_WIDTH;
  }

  _minHeight() {
    return Number.parseInt(this.getAttribute('data-min-height'), 10) || DEFAULT_MIN_HEIGHT;
  }

  _onPointerDown(e) {
    if (e.button !== 0) return;

    // Pointer-down anywhere on the window raises it; whether it also starts a
    // drag/resize is decided below.
    raiseWindow(this);

    // On small screens the window is full-screen, so there's nothing to move.
    if (window.innerWidth < SMALL_SCREEN) return;

    const resizeHandle = e.target.closest('[data-resize-handle]');
    if (resizeHandle && this.contains(resizeHandle)) {
      e.preventDefault();
      this.resizing = true;
      this.corner = resizeHandle.getAttribute('data-resize-handle') || 'se';
      const rect = this.getBoundingClientRect();
      this.startRect = rect;
      this.startOffsetX = this.offsetX;
      this.startOffsetY = this.offsetY;
      this.startX = e.clientX;
      this.startY = e.clientY;
      this._capture(e);
      return;
    }

    const handle = e.target.closest('[data-drag-handle]');
    if (!handle || !this.contains(handle)) return;
    if (e.target.closest('[data-no-drag]')) return;

    e.preventDefault();
    this.dragging = true;
    this.startX = e.clientX - this.offsetX;
    this.startY = e.clientY - this.offsetY;
    this._capture(e);
  }

  _onPointerMove(e) {
    if (e.pointerId !== this.pointerId) return;
    if (this.resizing) {
      this._resize(e);
    } else if (this.dragging) {
      this._drag(e);
    }
  }

  _resize(e) {
    const rect = this.startRect;
    // The handle names an edge or corner: "n"/"e"/"s"/"w" or a pair like "ne".
    const west = this.corner.includes('w');
    const east = this.corner.includes('e');
    const north = this.corner.includes('n');
    const south = this.corner.includes('s');
    const dx = e.clientX - this.startX;
    const dy = e.clientY - this.startY;

    // Width only changes for an east/west edge, height only for a north/south
    // edge — so an edge handle resizes a single axis and a corner resizes both.
    let width = rect.width;
    if (east) {
      width = clamp(rect.width + dx, this._minWidth(), window.innerWidth - rect.left - MARGIN);
    } else if (west) {
      width = clamp(rect.width - dx, this._minWidth(), rect.right - MARGIN);
    }

    let height = rect.height;
    if (south) {
      height = clamp(rect.height + dy, this._minHeight(), window.innerHeight - rect.top - MARGIN);
    } else if (north) {
      height = clamp(rect.height - dy, this._minHeight(), rect.bottom - MARGIN);
    }

    // For the top/left edges the opposite edge is fixed, so move the window by
    // however much the size actually changed (respecting the clamps above).
    this.offsetX = west ? this.startOffsetX + (rect.width - width) : this.startOffsetX;
    this.offsetY = north ? this.startOffsetY + (rect.height - height) : this.startOffsetY;

    this.style.width = `${width}px`;
    this.style.height = `${height}px`;
    this.style.transform = `translate(${this.offsetX}px, ${this.offsetY}px)`;
  }

  _drag(e) {
    this.offsetX = e.clientX - this.startX;
    this.offsetY = e.clientY - this.startY;
    this.style.transform = `translate(${this.offsetX}px, ${this.offsetY}px)`;
    this._clampIntoView();
  }

  // Keep the whole window within the viewport so it can't be dragged off-screen.
  // If the window is larger than the viewport, the top/left edges win (so the
  // header stays reachable) and the far edges overflow.
  _clampIntoView() {
    const rect = this.getBoundingClientRect();
    let dx = 0;
    let dy = 0;
    if (rect.left < 0) dx = -rect.left;
    else if (rect.right > window.innerWidth) dx = window.innerWidth - rect.right;
    if (rect.top < 0) dy = -rect.top;
    else if (rect.bottom > window.innerHeight) dy = window.innerHeight - rect.bottom;
    if (dx || dy) {
      this.offsetX += dx;
      this.offsetY += dy;
      this.style.transform = `translate(${this.offsetX}px, ${this.offsetY}px)`;
    }
  }

  _onViewportResize() {
    // A shrunk viewport (or a display change) can leave a window out of reach;
    // pull it back so the header stays grabbable.
    this._clampIntoView();
  }

  _capture(e) {
    this.pointerId = e.pointerId;
    try {
      this.setPointerCapture(e.pointerId);
    } catch {
      // setPointerCapture can throw if the pointer is already gone; the
      // pointerup/blur fallbacks still end the interaction cleanly.
    }
  }

  _onEnd() {
    if (!this.dragging && !this.resizing) return;
    this.dragging = false;
    this.resizing = false;
    if (this.pointerId !== null) {
      try {
        this.releasePointerCapture(this.pointerId);
      } catch {
        // Already released (e.g. the pointerup that preceded lostpointercapture).
      }
      this.pointerId = null;
    }
  }
}

if (!customElements.get('draggable-window')) {
  customElements.define('draggable-window', DraggableWindow);
}
